import { useQuery } from '@tanstack/react-query'
import { useSearch } from '@tanstack/react-router'
import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table'
import { BookOpen, CalendarClock, Layers, Plus } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { branchListQueryOptions } from '@/api/reference-api'
import { savingsAccountListQueryOptions } from '@/api/savings-api'
import type { SavingsAccount } from '@/api/savings-types'
import { useBranchSelection } from '@/components/app-shell/use-branch-selection'
import { DataTable, dataTableColumns, type DataTableColumn } from '@/components/data-table'
import { MoneyDisplay } from '@/components/money-display'
import { PageHeader } from '@/components/page-header'
import { StatusBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import { SavingsAccountFilters } from '@/features/savings/account-filters'
import { productClassLabel } from '@/features/savings/constants'
import { savingsPaths, useSavingsNavigate } from '@/features/savings/navigation'
import { OpenAccountDialog } from '@/features/savings/open-account-dialog'
import { useSavingsPermissions } from '@/features/savings/permissions'

import {
  DEFAULT_PAGE_SIZE,
  savingsAccountListSearchSchema,
  type SavingsAccountListSearch,
} from './search'

/**
 * The savings account list.
 *
 * Read-mostly, and it works on a phone: the tables stack into cards, and a customer telephoning
 * the branch to ask what their balance is produces exactly this lookup. Nothing on this screen
 * moves money — the screens that do refuse a narrow display, and they are one click further in.
 *
 * Nothing is sortable. The endpoint takes no ordering parameter, and ordering the page that
 * happens to be in the browser would be a claim about the whole result set that the next page
 * would immediately contradict.
 */
const UNSORTED: SortingState = []
const ignoreSortingChange: OnChangeFn<SortingState> = () => undefined

const helper = dataTableColumns<SavingsAccount>()

const columns: DataTableColumn<SavingsAccount>[] = helper.columns([
  helper.accessor('accountNumber', {
    header: 'Account no.',
    enableSorting: false,
    cell: (context) => <span className="font-mono text-xs">{context.row.original.accountNumber}</span>,
    meta: { noWrap: true, widthClass: 'w-40', card: 'lead' },
  }),
  helper.accessor('customerName', {
    header: 'Customer',
    enableSorting: false,
    cell: (context) => (
      <div className="min-w-0 space-y-0.5">
        <span className="font-medium text-heading">{context.row.original.customerName}</span>
        <p className="font-mono text-2xs text-muted">{context.row.original.customerNumber}</p>
      </div>
    ),
    meta: { card: 'title' },
  }),
  helper.accessor('productClass', {
    header: 'Class',
    enableSorting: false,
    cell: (context) => (
      <div className="space-y-0.5">
        <span>{productClassLabel(context.row.original.productClass)}</span>
        <p className="font-mono text-2xs text-muted">{context.row.original.productCode}</p>
      </div>
    ),
    meta: { noWrap: true, widthClass: 'w-40' },
  }),
  helper.accessor('ledgerBalanceMinor', {
    header: 'Ledger',
    enableSorting: false,
    cell: (context) => (
      <MoneyDisplay
        value={context.row.original.ledgerBalanceMinor}
        currency={context.row.original.currencyCode}
        symbol={false}
      />
    ),
    meta: { align: 'right', widthClass: 'w-32', cardLabel: 'Ledger balance' },
  }),
  helper.accessor('holdAmountMinor', {
    header: 'On hold',
    enableSorting: false,
    cell: (context) =>
      context.row.original.holdAmountMinor === 0 ? (
        <span className="block text-right text-muted">—</span>
      ) : (
        <MoneyDisplay
          value={context.row.original.holdAmountMinor}
          currency={context.row.original.currencyCode}
          symbol={false}
        />
      ),
    meta: { align: 'right', widthClass: 'w-32' },
  }),
  helper.accessor('availableBalanceMinor', {
    header: 'Available',
    enableSorting: false,
    cell: (context) => (
      <div className="space-y-0.5">
        <MoneyDisplay
          value={context.row.original.availableBalanceMinor}
          currency={context.row.original.currencyCode}
          symbol={false}
          emphasis="strong"
        />
        {!context.row.original.isWithdrawable ? (
          // Not a badge: the figure beside it is the thing being qualified, and a chip floating in
          // a numeric column reads as a status of the account rather than of the number.
          <p className="text-right text-2xs text-muted">not withdrawable</p>
        ) : null}
      </div>
    ),
    meta: { align: 'right', widthClass: 'w-36', card: 'lead' },
  }),
  helper.accessor('status', {
    header: 'Status',
    enableSorting: false,
    cell: (context) => <StatusBadge status={context.row.original.status} />,
    meta: { widthClass: 'w-28', card: 'lead' },
  }),
])

const SEARCH_DEBOUNCE_MS = 300

export function SavingsAccountsPage() {
  const navigate = useSavingsNavigate()
  const permissions = useSavingsPermissions()
  const { selectedBranchId } = useBranchSelection()

  // Parsed through the schema rather than read raw. Once the integrator attaches
  // `savingsAccountListSearchSchema` as the route's `validateSearch` this is a second pass over
  // already-validated values; until then it is what makes them typed and safe at all.
  const search: SavingsAccountListSearch = savingsAccountListSearchSchema.parse(
    useSearch({ strict: false }),
  )

  const branches = useQuery(branchListQueryOptions())
  const [searchText, setSearchText] = useState(search.search ?? '')
  const [opening, setOpening] = useState(false)

  const page = search.page ?? 1
  const pageSize = search.pageSize ?? DEFAULT_PAGE_SIZE

  const updateSearch = (patch: Partial<SavingsAccountListSearch>, replace = false) => {
    navigate(savingsPaths.accounts(), {
      search: {
        search: search.search,
        status: search.status,
        productClass: search.productClass,
        branchId: search.branchId,
        customerId: search.customerId,
        page: search.page,
        pageSize: search.pageSize,
        ...patch,
      },
      replace,
    })
  }

  // What this screen last wrote into the URL. Comparing against it is how a Back button, or a
  // pasted link, is mirrored into the box without the debounce writing it straight back out.
  const lastPushed = useRef(search.search ?? '')

  useEffect(() => {
    const fromUrl = search.search ?? ''
    if (fromUrl !== lastPushed.current) {
      lastPushed.current = fromUrl
      setSearchText(fromUrl)
    }
  }, [search.search])

  useEffect(() => {
    const trimmed = searchText.trim()
    if (trimmed === (search.search ?? '')) {
      return
    }

    const timer = window.setTimeout(() => {
      lastPushed.current = trimmed
      updateSearch({ search: trimmed === '' ? undefined : trimmed, page: undefined }, true)
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timer)
    }
    // Every filter is a dependency, not only the text: changing one mid-keystroke restarts the
    // debounce against the new values, so a timer left holding the old ones cannot land 300ms
    // later and undo the change the operator just made.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchText,
    search.search,
    search.status,
    search.productClass,
    search.branchId,
    search.customerId,
    search.pageSize,
  ])

  const query = useQuery(
    savingsAccountListQueryOptions({
      search: search.search,
      status: search.status,
      productClass: search.productClass,
      branchId: search.branchId,
      customerId: search.customerId,
      page,
      pageSize,
    }),
  )

  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const current: PaginationState = { pageIndex: page - 1, pageSize }
    const next = typeof updater === 'function' ? updater(current) : updater
    updateSearch({ page: next.pageIndex + 1, pageSize: next.pageSize })
  }

  const hasActiveFilters = Boolean(
    search.search ?? search.status ?? search.productClass ?? search.branchId ?? search.customerId,
  )

  const clearFilters = () => {
    lastPushed.current = ''
    setSearchText('')
    navigate(savingsPaths.accounts(), { search: { pageSize: search.pageSize } })
  }

  return (
    <>
      <PageHeader
        title="Savings"
        description="Passbook savings and the security deposits that collateralise group loans. Two classes of account on two separate general-ledger liabilities, because the Bank of Ghana deposit return reports them separately."
        actions={
          <>
            {permissions.canViewProducts ? (
              <Button variant="secondary" onClick={() => navigate(savingsPaths.products())}>
                <Layers />
                Products
              </Button>
            ) : null}
            {permissions.canPostInterest || permissions.canManageDormancy ? (
              <Button variant="secondary" onClick={() => navigate(savingsPaths.runs())}>
                <CalendarClock />
                Period-end runs
              </Button>
            ) : null}
            {permissions.canOpenAccount ? (
              <Button variant="primary" onClick={() => setOpening(true)}>
                <Plus />
                Open an account
              </Button>
            ) : null}
          </>
        }
      />

      <div className="space-y-3">
        <SavingsAccountFilters
          searchText={searchText}
          onSearchTextChange={setSearchText}
          status={search.status}
          onStatusChange={(status) => {
            updateSearch({ status, page: undefined })
          }}
          productClass={search.productClass}
          onProductClassChange={(productClass) => {
            updateSearch({ productClass, page: undefined })
          }}
          branchId={search.branchId}
          onBranchChange={(branchId) => {
            updateSearch({ branchId, page: undefined })
          }}
          branches={branches.data ?? []}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={clearFilters}
        />

        {search.customerId ? (
          <p className="text-xs text-muted">
            Filtered to one customer.{' '}
            <button
              type="button"
              className="font-medium text-brand-text underline underline-offset-2"
              onClick={() => navigate(savingsPaths.customer(search.customerId ?? ''))}
            >
              Open their savings record
            </button>{' '}
            to reach their passbooks and loan gates.
          </p>
        ) : null}

        <DataTable
          columns={columns}
          data={query.data?.items}
          rowCount={query.data?.totalCount}
          pagination={{ pageIndex: page - 1, pageSize }}
          onPaginationChange={onPaginationChange}
          sorting={UNSORTED}
          onSortingChange={ignoreSortingChange}
          isLoading={query.isPending}
          isFetching={query.isFetching}
          error={query.isError ? query.error : null}
          onRetry={() => void query.refetch()}
          getRowId={(row) => row.id}
          onRowActivate={(account) => {
            navigate(savingsPaths.account(account.id))
          }}
          pageSizeOptions={[DEFAULT_PAGE_SIZE, 50, 100, 200]}
          emptyTitle={hasActiveFilters ? 'No accounts match' : 'No savings accounts yet'}
          emptyDescription={
            hasActiveFilters
              ? 'Nothing was found with those filters. The list only reaches branches your role can see.'
              : 'Nobody at the branches you can see holds a savings or security deposit account. The first one starts with a product and a customer.'
          }
          emptyAction={
            hasActiveFilters ? (
              <Button variant="secondary" size="sm" onClick={clearFilters}>
                Clear the filters
              </Button>
            ) : permissions.canOpenAccount ? (
              <Button variant="primary" size="sm" onClick={() => setOpening(true)}>
                <BookOpen />
                Open an account
              </Button>
            ) : null
          }
        />
      </div>

      <OpenAccountDialog
        customerId={search.customerId ?? null}
        branchId={selectedBranchId}
        open={opening}
        onOpenChange={setOpening}
      />
    </>
  )
}
